import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/**
 * Stage 2A — Team Direction editor (NEW; deliberately NOT the frozen
 * assignment-down `SetTeamPrioritiesDialog`). It edits ONLY team-level direction
 * fields: title, description, importance. There is NO assignee / member selector
 * / per-person status / done / carry-forward field, and no link/momentum field.
 */
export interface TeamPriorityEditorValues {
  title: string;
  description: string;
  importance: number;
}

export interface TeamPriorityEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initial?: TeamPriorityEditorValues;
  isAr: boolean;
  busy?: boolean;
  onSubmit: (values: TeamPriorityEditorValues) => void;
}

export function TeamPriorityEditorDialog({
  open,
  onOpenChange,
  mode,
  initial,
  isAr,
  busy,
  onSubmit,
}: TeamPriorityEditorDialogProps) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [importance, setImportance] = useState(initial?.importance ?? 2);

  // Reset the form whenever the dialog (re)opens for a given target.
  useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? "");
      setDescription(initial?.description ?? "");
      setImportance(initial?.importance ?? 2);
    }
  }, [open, initial?.title, initial?.description, initial?.importance]);

  const canSubmit = title.trim().length > 0 && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="td-editor">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit"
              ? t("Edit team priority", "تعديل أولوية الفريق")
              : t("New team priority", "أولوية فريق جديدة")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Shared team direction. Not assigned to anyone.",
              "توجيه مُشترَك للفريق. غير مُسنَد لأي شخص.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="td-title-input">{t("Title", "العنوان")}</Label>
            <Input
              id="td-title-input"
              data-testid="td-editor-title"
              value={title}
              maxLength={280}
              onChange={e => setTitle(e.target.value)}
              placeholder={t("What is the team focusing on?", "على ماذا يركّز الفريق؟")}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-desc-input">{t("Description", "الوصف")}</Label>
            <Textarea
              id="td-desc-input"
              data-testid="td-editor-description"
              value={description}
              maxLength={2000}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-importance-input">{t("Importance", "الأهمية")}</Label>
            <select
              id="td-importance-input"
              data-testid="td-editor-importance"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={importance}
              onChange={e => setImportance(Number(e.target.value))}
            >
              <option value={1}>{t("High", "عالٍ")}</option>
              <option value={2}>{t("Medium", "متوسط")}</option>
              <option value={3}>{t("Low", "منخفض")}</option>
            </select>
          </div>
        </div>

        {/* Clear breathing room between the Importance select and the primary
            action (mobile-polish spacing) — never glued to the field above. */}
        <DialogFooter className="mt-4" data-testid="td-editor-footer">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="td-editor-cancel"
          >
            {t("Cancel", "إلغاء")}
          </Button>
          <Button
            disabled={!canSubmit}
            data-testid="td-editor-submit"
            onClick={() =>
              onSubmit({
                title: title.trim(),
                description: description.trim(),
                importance,
              })
            }
          >
            {mode === "edit" ? t("Save", "حفظ") : t("Create", "إنشاء")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
